/**
 * Trunnion AI - Notification Store
 * Toast notifications, persistent notification history, and approval request queue.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  persistent?: boolean;
  timestamp: number;
  read?: boolean;
}

export interface ApprovalQueueItem {
  id: string;
  executionId: string;
  stepId: string;
  description: string;
  requiredRoles: string[];
  expiresAt: string;
  timestamp: string;
}

const MAX_HISTORY = 100;

interface NotificationState {
  notifications: Notification[];
  notificationHistory: Notification[];
  approvalQueue: ApprovalQueueItem[];
  unreadCount: number;

  addNotification: (
    notification: Omit<Notification, 'id' | 'timestamp' | 'read'>,
  ) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  removeFromHistory: (id: string) => void;
  clearHistory: () => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  addApprovalRequest: (item: Omit<ApprovalQueueItem, 'id'>) => void;
  removeApprovalRequest: (id: string) => void;
  clearApprovalQueue: () => void;
}

let notificationCounter = 0;

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      notificationHistory: [],
      approvalQueue: [],
      unreadCount: 0,

      addNotification: (notification) => {
        const id = `notification-${++notificationCounter}`;
        const newNotification: Notification = {
          ...notification,
          id,
          timestamp: Date.now(),
          read: false,
        };

        set({
          notifications: [...get().notifications, newNotification],
          notificationHistory: [
            newNotification,
            ...get().notificationHistory,
          ].slice(0, MAX_HISTORY),
          unreadCount: get().unreadCount + 1,
        });

        // Auto-remove non-persistent notifications after 5 seconds
        if (!notification.persistent) {
          setTimeout(() => {
            set({
              notifications: get().notifications.filter((n) => n.id !== id),
            });
          }, 5_000);
        }
      },

      removeNotification: (id) =>
        set({
          notifications: get().notifications.filter((n) => n.id !== id),
        }),

      clearNotifications: () => set({ notifications: [] }),

      removeFromHistory: (id) => {
        const item = get().notificationHistory.find((n) => n.id === id);
        const wasUnread = item && !item.read;
        set({
          notificationHistory: get().notificationHistory.filter(
            (n) => n.id !== id,
          ),
          unreadCount: wasUnread
            ? Math.max(0, get().unreadCount - 1)
            : get().unreadCount,
        });
      },

      clearHistory: () =>
        set({
          notificationHistory: [],
          unreadCount: 0,
        }),

      markRead: (id) => {
        const item = get().notificationHistory.find((n) => n.id === id);
        if (item && !item.read) {
          set({
            notificationHistory: get().notificationHistory.map((n) =>
              n.id === id ? { ...n, read: true } : n,
            ),
            unreadCount: Math.max(0, get().unreadCount - 1),
          });
        }
      },

      markAllRead: () =>
        set({
          notificationHistory: get().notificationHistory.map((n) => ({
            ...n,
            read: true,
          })),
          unreadCount: 0,
        }),

      addApprovalRequest: (item) => {
        const id = `approval-${item.executionId}-${item.stepId}`;
        // Avoid duplicates
        if (get().approvalQueue.some((a) => a.id === id)) return;
        set({
          approvalQueue: [...get().approvalQueue, { ...item, id }],
        });
      },

      removeApprovalRequest: (id) =>
        set({
          approvalQueue: get().approvalQueue.filter((a) => a.id !== id),
        }),

      clearApprovalQueue: () => set({ approvalQueue: [] }),
    }),
    {
      name: 'trunnion-notifications',
      partialize: (state) => ({
        notificationHistory: state.notificationHistory,
        unreadCount: state.unreadCount,
      }),
    },
  ),
);
